import KTComponent from "../component";
import { KTDatepickerStateManager } from "./config";
import { ActiveInput, DatePickerSegment, KTDatepickerConfigInterface } from "./types";

export class KTDatepickerKeyboard extends KTComponent {
	protected override readonly _name: string = 'datepicker-keyboard';
	protected override readonly _config: KTDatepickerConfigInterface;

	private _state: KTDatepickerStateManager;

	private _dateInputElement: HTMLInputElement;
	private _dateInputDisplayElement: HTMLElement;

	private _focusedSegment: string;
	private _activeInput: ActiveInput;

	constructor(element: HTMLElement, stateManager?: KTDatepickerStateManager) {
		super();
		this._state = stateManager;

		this._init(element);
		this._buildConfig(this._state.getConfig());

		this._attachInputEventListeners();
		this._attachKeyboardEvent();
	}

	private _attachInputEventListeners() {
		this._element.addEventListener(this._config.dateChangeEvent, this._handleOnDateChange.bind(this));
	}

	private _handleOnDateChange(_event: CustomEvent) {
		this._dispatchEvent(this._config.calendarRenderedEvent, this._state.getSelectedDateInterface());
		this._fireEvent(this._config.calendarRenderedEvent, this._state.getSelectedDateInterface());
	}

	private _dateInputFocusOut(_event: Event) {
		this._focusedSegment = null;
	}

	private _updateDateInputDisplayElement() {
		this._element.querySelectorAll(`[${this._config.segmentSelector}]`).forEach(element => {
			if (element instanceof HTMLElement) {
				element.classList.remove(this._config.selectedClass);
			}
		});

		if (this._focusedSegment) {
			this._element.querySelector(`[${this._config.segmentSelector}="${this._focusedSegment}"]`)?.classList.add(this._config.selectedClass);
		}

		if (this._dateInputDisplayElement) {
			this._dateInputDisplayElement.addEventListener('click', this._handleDateInputSegmentClick.bind(this));
		}

		if (this._dateInputElement) {
			this._dateInputElement.addEventListener('focusout', this._dateInputFocusOut.bind(this));
		}
	}

	private _handleDateInputSegmentClick(event: Event): void {
		this._element.querySelectorAll(`[${this._config.segmentSelector}]`).forEach(segmentElement => {
			segmentElement?.classList.remove(this._config.selectedClass);
			if (segmentElement.contains(event.target as HTMLElement)) {
				segmentElement?.classList.add(this._config.selectedClass);
				this._focusedSegment = (segmentElement as HTMLElement).dataset.datepickerSegment as string;
			}
		});
		this._dateInputElement.focus();
	}


	/**
	 * ========================================================================
	 * Keyboard navigation on input
	 * ========================================================================
	 */

	private _attachKeyboardEvent(): void {
		this._element.addEventListener('keydown', (event) => {
			event.preventDefault();

			if (this._focusedSegment) {
				this._handleKeyboardNavigation(event.key);
				this._updateDateInputDisplayElement();
			} else {
				this._handleKeyboardNavigationCalendar(event.key);
			}
		});
	}

	private _handleKeyboardNavigation(key: string): void {
		// Check if the pressed key is a number or backspace
		if (/\d/.test(key) || key === 'Backspace') {
			this._handleKeyboardInput(key);
		} else {
			const segmentActions: Record<string, () => void> = {
				ArrowUp: () => this._adjustSegmentValue(1),
				ArrowDown: () => this._adjustSegmentValue(-1),
				ArrowLeft: () => this._navigateSegment(-1),
				ArrowRight: () => this._navigateSegment(1),
				Enter: () => this._handleEnterKey(),
				Escape: () => this._focusedSegment = null,
			};

			segmentActions[key]?.();
		}

		this._dispatchEvent(this._config.dateChangeEvent, this._state.getSelectedDateInterface());
		this._fireEvent(this._config.dateChangeEvent, this._state.getSelectedDateInterface());

		this._dispatchEvent(this._config.keyboardNavigateEvent, this._state.getSelectedDateInterface());
		this._fireEvent(this._config.keyboardNavigateEvent, this._state.getSelectedDateInterface());
	}

	private _adjustSegmentValue(increment: number): void {
		const activeDate = this._getActiveDate() || new Date();

		switch (this._focusedSegment) {
			case DatePickerSegment.Day:
			case DatePickerSegment.EndDay:
				const maxDay = new Date(activeDate.getFullYear(), activeDate.getMonth() + 1, 0).getDate();
				const newDay = activeDate.getDate() + increment;
				if (newDay >= 1 && newDay <= maxDay) {
					activeDate.setDate(newDay);
				}
				break;
			case DatePickerSegment.Month:
			case DatePickerSegment.EndMonth:
				activeDate.setMonth(activeDate.getMonth() + increment);
				break;
			case DatePickerSegment.Year:
			case DatePickerSegment.EndYear:
				activeDate.setFullYear(activeDate.getFullYear() + increment);
				break;
			case DatePickerSegment.Hours:
			case DatePickerSegment.EndHours:
				let newHours = activeDate.getHours() + increment;
				newHours = (newHours + 24) % 24; // Ensure hours stay within 0-23 range
				activeDate.setHours(newHours);
				break;
			case DatePickerSegment.Minutes:
			case DatePickerSegment.EndMinutes:
				let newMinutes = activeDate.getMinutes() + increment;
				newMinutes = (newMinutes + 60) % 60; // Ensure minutes stay within 0-59 range
				activeDate.setMinutes(newMinutes);
				break;
			case DatePickerSegment.Seconds:
			case DatePickerSegment.EndSeconds:
				let newSeconds = activeDate.getSeconds() + increment;
				newSeconds = (newSeconds + 60) % 60; // Ensure seconds stay within 0-59 range
				activeDate.setSeconds(newSeconds);
				break;
			case DatePickerSegment.AmPm:
			case DatePickerSegment.EndAmPm:
				activeDate.setHours(activeDate.getHours() + 12 * increment);
				break;
		}

		this._updateActiveDate(activeDate);
	}

	private _handleKeyboardInput(key: string) {
		const activeDate = this._getActiveDate() ?? new Date();
		const segment = this._focusedSegment as DatePickerSegment;

		let currentValue = this._getSegmentValue(activeDate, segment);
		let maxValue = this._getSegmentMaxValue(activeDate, segment);
		let requiredDigits = this._getSegmentRequiredDigits(segment);

		if (key === 'Backspace') {
			currentValue = currentValue > 9 ? Math.floor(currentValue / 10) : 0;
		} else {
			const newDigit = parseInt(key);
			const potentialNewValue = currentValue * 10 + newDigit;

			if (potentialNewValue > maxValue) {
				currentValue = newDigit;
			} else if (potentialNewValue >= this._getSegmentMinValue(segment)) {
				currentValue = potentialNewValue;
			}
		}

		this._setSegmentValue(activeDate, segment, currentValue);

		// Navigate to the next segment if:
		// 1. It's the month segment (start or end) AND the current value is NOT 1
		// 2. OR the number of digits entered matches the required digits for the segment
		if (((segment === DatePickerSegment.Month || segment === DatePickerSegment.EndMonth) && currentValue !== 1)
			|| currentValue.toString().length === requiredDigits) {
			this._navigateSegment(1);
		}

		this._updateActiveDate(activeDate);
	}

	private _getSegmentRequiredDigits(segment: DatePickerSegment): number {
		switch (segment) {
			case DatePickerSegment.Day:
			case DatePickerSegment.EndDay:
			case DatePickerSegment.Month:
			case DatePickerSegment.EndMonth:
			case DatePickerSegment.Hours:
			case DatePickerSegment.EndHours:
			case DatePickerSegment.Minutes:
			case DatePickerSegment.EndMinutes:
			case DatePickerSegment.Seconds:
			case DatePickerSegment.EndSeconds:
				return 2;
			case DatePickerSegment.Year:
			case DatePickerSegment.EndYear:
				return 4;
			default:
				return 0;
		}
	}

	private _getSegmentValue(date: Date, segment: DatePickerSegment): number {
		switch (segment) {
			case DatePickerSegment.Day:
			case DatePickerSegment.EndDay:
				return date.getDate();
			case DatePickerSegment.Month:
			case DatePickerSegment.EndMonth:
				return date.getMonth() + 1; // Months are 0-indexed
			case DatePickerSegment.Year:
			case DatePickerSegment.EndYear:
				return date.getFullYear();
			case DatePickerSegment.Hours:
			case DatePickerSegment.EndHours:
				return date.getHours();
			case DatePickerSegment.Minutes:
			case DatePickerSegment.EndMinutes:
				return date.getMinutes();
			case DatePickerSegment.Seconds:
			case DatePickerSegment.EndSeconds:
				return date.getSeconds();
			default:
				return 0;
		}
	}

	private _getSegmentMaxValue(date: Date, segment: DatePickerSegment): number {
		switch (segment) {
			case DatePickerSegment.Day:
			case DatePickerSegment.EndDay:
				return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
			case DatePickerSegment.Month:
			case DatePickerSegment.EndMonth:
				return 12;
			case DatePickerSegment.Year:
			case DatePickerSegment.EndYear:
				return 9999; // Or any reasonable upper limit you want to set
			case DatePickerSegment.Hours:
			case DatePickerSegment.EndHours:
				return 23;
			case DatePickerSegment.Minutes:
			case DatePickerSegment.EndMinutes:
			case DatePickerSegment.Seconds:
			case DatePickerSegment.EndSeconds:
				return 59;
			default:
				return 0;
		}
	}

	private _getSegmentMinValue(segment: DatePickerSegment): number {
		switch (segment) {
			case DatePickerSegment.Day:
			case DatePickerSegment.EndDay:
			case DatePickerSegment.Month:
			case DatePickerSegment.EndMonth:
				return 1;
			case DatePickerSegment.Year:
			case DatePickerSegment.EndYear:
				return 1; // Or any reasonable lower limit you want to set
			case DatePickerSegment.Hours:
			case DatePickerSegment.EndHours:
			case DatePickerSegment.Minutes:
			case DatePickerSegment.EndMinutes:
			case DatePickerSegment.Seconds:
			case DatePickerSegment.EndSeconds:
				return 0;
			default:
				return 0;
		}
	}

	private _setSegmentValue(date: Date, segment: DatePickerSegment, value: number): void {
		switch (segment) {
			case DatePickerSegment.Day:
			case DatePickerSegment.EndDay:
				date.setDate(value);
				break;
			case DatePickerSegment.Month:
			case DatePickerSegment.EndMonth:
				date.setMonth(value - 1); // Months are 0-indexed
				break;
			case DatePickerSegment.Year:
			case DatePickerSegment.EndYear:
				date.setFullYear(value);
				break;
			case DatePickerSegment.Hours:
			case DatePickerSegment.EndHours:
				date.setHours(value);
				break;
			case DatePickerSegment.Minutes:
			case DatePickerSegment.EndMinutes:
				date.setMinutes(value);
				break;
			case DatePickerSegment.Seconds:
			case DatePickerSegment.EndSeconds:
				date.setSeconds(value);
				break;
		}
	}

	private _getActiveDate(): Date {
		// Set default activeInput to 'end' for range selection
		this._activeInput = this._config.range ? ActiveInput.End : ActiveInput.Start;

		// Check if the selected segment belongs to the start date
		const isStartDateSegment = Object.values(DatePickerSegment)
			.slice(0, Object.keys(DatePickerSegment).length / 2) // Get only the start date segments
			.includes(this._focusedSegment as DatePickerSegment);

		if (isStartDateSegment) {
			this._activeInput = ActiveInput.Start;
		}

		const { selectedDate, selectedDateRange } = this._state.getSelectedDateInterface();

		return this._config.range ?
			(this._activeInput === ActiveInput.Start ? selectedDateRange?.startDate : selectedDateRange?.endDate)
			: selectedDate;
	}

	private _updateActiveDate(date: Date): void {
		if (this._config.range) {
			if (this._activeInput === ActiveInput.Start) {
				this._state.setSelectedDateRangeStart(new Date(date));
			} else {
				this._state.setSelectedDateRangeEnd(new Date(date));
			}
		} else {
			this._state.setSelectedDate(new Date(date));
		}
	}

	private _navigateSegment(offset: number): void {
		const segmentValues = Object.values(DatePickerSegment) as DatePickerSegment[];
		const segmentCount = segmentValues.length;
		let currentIndex = segmentValues.indexOf(this._focusedSegment as DatePickerSegment);

		// Calculate the new index, ensuring it stays within bounds and skips time segments if not enabled
		let newIndex = currentIndex + offset;
		while (newIndex < 0 || newIndex >= segmentCount
			|| (!this._config.enableTime && this._isTimeSegment(segmentValues[newIndex]))
			|| (!this._config.range && this._isOutOfRangeSegment(segmentValues[newIndex])) // Skip out-of-range segments if in range mode
		) {
			newIndex += offset; // Keep adjusting until a valid segment is found
			if (newIndex < 0) {
				newIndex = segmentCount - 1;
			} else if (newIndex >= segmentCount) {
				newIndex = 0;
			}
		}

		this._focusedSegment = segmentValues[newIndex];
	}

	private _isOutOfRangeSegment(segment: DatePickerSegment): boolean {
		return [
			DatePickerSegment.EndDay,
			DatePickerSegment.EndMonth,
			DatePickerSegment.EndYear,
			DatePickerSegment.EndHours,
			DatePickerSegment.EndMinutes,
			DatePickerSegment.EndSeconds,
			DatePickerSegment.EndAmPm,
			DatePickerSegment.GmtOffset,
		].includes(segment);
	}

	private _isTimeSegment(segment: DatePickerSegment): boolean {
		return [
			DatePickerSegment.Hours,
			DatePickerSegment.EndHours,
			DatePickerSegment.Minutes,
			DatePickerSegment.EndMinutes,
			DatePickerSegment.Seconds,
			DatePickerSegment.EndSeconds,
			DatePickerSegment.AmPm,
			DatePickerSegment.EndAmPm,
			DatePickerSegment.GmtOffset,
		].includes(segment);
	}

	private _handleEnterKey() {
		console.log('enter');
	}


	/**
	 * ========================================================================
	 * Keyboard navigation on calendar
	 * ========================================================================
	 */

	private _handleKeyboardNavigationCalendar(key: string) {
		let activeDate;
		if (this._config.range) {
			const { startDate, endDate } = this._state.getSelectedDateRange();
			activeDate = endDate < startDate ? startDate : endDate;
		} else {
			activeDate = this._state.getSelectedDate();
		}
		if (!activeDate) {
			activeDate = new Date();
		}

		const segmentActions: Record<string, () => void> = {
			ArrowUp: () => activeDate.setDate(activeDate.getDate() - 7),
			ArrowDown: () => activeDate.setDate(activeDate.getDate() + 7),
			ArrowLeft: () => activeDate.setDate(activeDate.getDate() - 1),
			ArrowRight: () => activeDate.setDate(activeDate.getDate() + 1),
			Enter: () => {
				if (this._config.range) {
					const { startDate, endDate } = this._state.getSelectedDateRange();
					if (!startDate) {
						this._state.setSelectedDateRangeStart(new Date());
					}
					if (!endDate) {
						this._state.setSelectedDateRangeEnd(new Date(Date.now() + 86400000));
					}
				} else {
					const selectedDate = this._state.getSelectedDate();
					if (!selectedDate) {
						this._state.setSelectedDate(new Date());
					}
				}
			},
			Escape: () => {
				this._focusedSegment = null;
				this._dateInputElement.blur();
			},
		};

		segmentActions[key]?.();

		this._dispatchEvent(this._config.dateChangeEvent, this._state.getSelectedDateInterface());
		this._fireEvent(this._config.dateChangeEvent, this._state.getSelectedDateInterface());

		this._dispatchEvent(this._config.keyboardNavigateEvent, this._state.getSelectedDateInterface());
		this._fireEvent(this._config.keyboardNavigateEvent, this._state.getSelectedDateInterface());
	}


	/**
	 * ========================================================================
	 * Public API
	 * ========================================================================
	 */

	public setInputDisplayElement(element: HTMLElement): void {
		this._dateInputDisplayElement = element;
		this._updateDateInputDisplayElement();
	}

	public setInputElement(element: HTMLInputElement): void {
		this._dateInputElement = element;
	}
}